import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import * as Transforms from '@app/utils/transforms';
import { UnitSetupConfig } from './unit-setup-config.interface';

export const salesTaxTabFields = (config: UnitSetupConfig): FormlyFieldConfig[] => {
	return [
		{
			wrappers: [FORMLY_FIELD_WRAPPERS.customWrapper],
			fieldGroup: [
				{
					type: FORMLY_FIELD_TYPES.headerLabel,
					props: {
						label: 'Tax Information',
						useElementH: '5',
						useSecondaryColor: true,
						horizontalLine: true,
						bold: true,
						useDefaultLineColor: true,
						marginBottom: 4,
					},
				},
				{
					// Rates Header Display
					fieldGroupClassName: 'row',
					fieldGroup: ratesHeaderFieldGroup(),
				},
				{
					// Goods Row Display
					fieldGroupClassName: 'row',
					fieldGroup: goodsRowFieldGroup(config),
				},
				{
					// Services Row Display
					fieldGroupClassName: 'row',
					fieldGroup: servicesRowFieldGroup(config),
				},
				{
					// Food Row Display
					fieldGroupClassName: 'row',
					fieldGroup: foodRowFieldGroup(config),
				},
				{
					// Vendor Row Display
					fieldGroupClassName: 'row linq-mt-10',
					fieldGroup: [
						{
							type: FORMLY_FIELD_TYPES.multiuseSearchInput,
							key: 'SalesTaxVendorId',
							className: 'col-sm-6',
							props: {
								label: 'Sales Tax Vendor',
								placeholder: 'Search Sales Tax Vendor',
								hideLabel: false,
								required: false,
								readonly: config.isReadOnly,
								defaultInputValue: '',
								dropdownProperties: ['ShortName', 'VendorName', 'ParentVendorShortName', 'ParentVendorName'],
								dropdownTemplate: 'ShortName - VendorName<br>Parent: ParentVendorShortName - ParentVendorName',
								inputTemplate: 'ShortName - VendorName',
								keyValueProperty: 'Id',
								filter: (value: string) => config.vendorSearchFilter(value),
							},
							hooks: {
								// When loading - get the vendor information based off Property: SalesTaxVendorId
								onInit: async (field: FieldTypeConfig) => config.initSalesTaxVendor(field),
							},
						},
					],
				},
				{
					// Sales Tax Due Site Row Display
					fieldGroupClassName: 'row',
					fieldGroup: [
						{
							type: FORMLY_FIELD_TYPES.select,
							key: 'SalesTaxDueSiteId',
							className: 'col-sm-6',
							props: {
								label: 'Sales Tax Due Site',
								required: false,
								disabled: config.isReadOnly,
							},
							expressions: {
								'props.options': 'formState.selects.sites',
							},
						},
					],
				},
			],
		},
	];
};

const ratesHeaderFieldGroup = (): FormlyFieldConfig[] => {
	return [
		{
			template: `<div class="col-md-2"></div>`,
		},
		{
			type: FORMLY_FIELD_TYPES.headerLabel,
			className: 'col-md-2',
			props: {
				label: 'State Rate',
				useElementH: '5',
				useSecondaryColor: true,
				marginBottom: 2,
			},
		},
		{
			type: FORMLY_FIELD_TYPES.headerLabel,
			className: 'col-md-2',
			props: {
				label: 'County Rate',
				useElementH: '5',
				useSecondaryColor: true,
				marginBottom: 2,
			},
		},
		{
			type: FORMLY_FIELD_TYPES.headerLabel,
			className: 'col-md-2',
			props: {
				label: 'Local Rate',
				useElementH: '5',
				useSecondaryColor: true,
				marginBottom: 2,
			},
		},
	];
};

const goodsRowFieldGroup = (config: UnitSetupConfig): FormlyFieldConfig[] => {
	return [
		{
			type: FORMLY_FIELD_TYPES.headerLabel,
			className: 'col-md-2',
			props: {
				label: 'Goods',
				useElementH: '5',
				useSecondaryColor: true,
				marginTop: 2,
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'StateTaxRateGoods',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'CountyTaxRateGoods',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'LocalTaxRateGoods',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
	];
};

const servicesRowFieldGroup = (config: UnitSetupConfig): FormlyFieldConfig[] => {
	return [
		{
			type: FORMLY_FIELD_TYPES.headerLabel,
			className: 'col-md-2',
			props: {
				label: 'Services',
				useElementH: '5',
				useSecondaryColor: true,
				marginTop: 2,
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'StateTaxRateService',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'CountyTaxRateService',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'LocalTaxRateService',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
	];
};

const foodRowFieldGroup = (config: UnitSetupConfig): FormlyFieldConfig[] => {
	return [
		{
			type: FORMLY_FIELD_TYPES.headerLabel,
			className: 'col-md-2',
			props: {
				label: 'Food',
				useElementH: '5',
				useSecondaryColor: true,
				marginTop: 2,
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'StateTaxRateFood',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'CountyTaxRateFood',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
		{
			type: FORMLY_FIELD_TYPES.decimal2Input,
			className: 'col-md-2',
			key: 'LocalTaxRateFood',
			props: {
				required: true,
				readonly: config.isReadOnly,
			},
			hooks: {
				onInit: (field: FieldTypeConfig) => {
					Transforms.applyDecimalFormat(field, 2);
				},
			},
		},
	];
};
